import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Grid, MenuItem, Typography, Box,
    IconButton, Divider, Chip, Autocomplete,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { ProductPlanPanel } from 'components/ProductPlanPanel';

interface EnvConfig {
    countries: { code: string; label: string; dialCode: string }[];
    currencies: { code: string; symbol: string; label: string }[];
    countryToCurrency: Record<string, string>;
    licenseTypes: string[];
    modules: string[];
    rootDomains: string[];
}

interface ProductPlanForm {
    licenseType: string;
    module: string;
    noOfUsers: number;
    durationMonths: number;
    whatsAppPerDay: number;
    whatsAppTotalCredit: number;
    whatsAppTemplatePerDay: number;
    whatsAppTemplateTotalCredit: number;
    whatsAppBusinessPerDay: number;
    whatsAppBusinessTotalCredit: number;
    whatsAppBusinessTemplatePerDay: number;
    whatsAppBusinessTemplateTotalCredit: number;
    gsmTextPerDay: number;
    gsmTextTotalCredit: number;
    gsmTextTemplatePerDay: number;
    gsmTextTemplateTotalCredit: number;
    telegramPerDay: number;
    telegramTotalCredit: number;
    telegramTemplatePerDay: number;
    telegramTemplateTotalCredit: number;
    emailPerDay: number;
    emailTotalCredit: number;
    emailTemplatePerDay: number;
    emailTemplateTotalCredit: number;
    senderHeaderName: string;
    gsmWorkerName: string;
    totalSizeInPlan?: number;
}

interface UpgradeRoute {
    targetPlan: string;
    targetCycle: 'monthly' | 'annual';
}

interface FormData {
    planName: string;
    tagline: string;
    country: string;
    rootDomain: string;
    monthlyPrice: number;
    annualMonthlyPrice: number;
    upgradeRoutesMonthly: UpgradeRoute[];
    upgradeRoutesAnnual: UpgradeRoute[];
    downgradeRoutesMonthly: UpgradeRoute[];
    downgradeRoutesAnnual: UpgradeRoute[];
    productPlans: ProductPlanForm[];
}

interface BusinessPlanDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    plan: any | null;
    envConfig: EnvConfig;
    allPlans?: { planName: string; country: string; rootDomain: string }[];
}

const emptyProductPlan = (): ProductPlanForm => ({
    licenseType: '',
    module: '',
    noOfUsers: 1,
    durationMonths: 1,
    whatsAppPerDay: 0,
    whatsAppTotalCredit: 0,
    whatsAppTemplatePerDay: 0,
    whatsAppTemplateTotalCredit: 0,
    whatsAppBusinessPerDay: 0,
    whatsAppBusinessTotalCredit: 0,
    whatsAppBusinessTemplatePerDay: 0,
    whatsAppBusinessTemplateTotalCredit: 0,
    gsmTextPerDay: 0,
    gsmTextTotalCredit: 0,
    gsmTextTemplatePerDay: 0,
    gsmTextTemplateTotalCredit: 0,
    telegramPerDay: 0,
    telegramTotalCredit: 0,
    telegramTemplatePerDay: 0,
    telegramTemplateTotalCredit: 0,
    emailPerDay: 0,
    emailTotalCredit: 0,
    emailTemplatePerDay: 0,
    emailTemplateTotalCredit: 0,
    senderHeaderName: '',
    gsmWorkerName: '',
});

const emptyRoute = (): UpgradeRoute => ({ targetPlan: '', targetCycle: 'monthly' });

const initialFormData = (): FormData => ({
    planName: '',
    tagline: '',
    country: '',
    rootDomain: '',
    monthlyPrice: 0,
    annualMonthlyPrice: 0,
    upgradeRoutesMonthly: [],
    upgradeRoutesAnnual: [],
    downgradeRoutesMonthly: [],
    downgradeRoutesAnnual: [],
    productPlans: [],
});

export const BusinessPlanDialog: React.FC<BusinessPlanDialogProps> = ({ open, onClose, onSave, plan, envConfig, allPlans = [] }) => {
    const isEditing = !!plan;
    const [formData, setFormData] = useState<FormData>(initialFormData());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [workerPoolNames, setWorkerPoolNames] = useState<string[]>([]);
    const [workerPoolNamesLoading, setWorkerPoolNamesLoading] = useState(false);
    const [workerPoolNamesError, setWorkerPoolNamesError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        const fetchWorkerPoolNames = async () => {
            try {
                setWorkerPoolNamesLoading(true);
                setWorkerPoolNamesError(null);
                const result = await apiClient.get('/iu/workerpool/names');
                const names = (result as any)?.data || result || [];
                setWorkerPoolNames(Array.isArray(names) ? names.map((n: any) => String(n)) : []);
            } catch (e: any) {
                setWorkerPoolNamesError(typeof e?.message === 'string' ? e.message : 'Failed to load worker pool names');
                setWorkerPoolNames([]);
            } finally {
                setWorkerPoolNamesLoading(false);
            }
        };
        fetchWorkerPoolNames();
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (plan) {
            setFormData({
                planName: plan.planName || '',
                tagline: plan.tagline || '',
                country: plan.country || '',
                rootDomain: plan.rootDomain || '',
                monthlyPrice: plan.pricing?.monthlyPrice ?? 0,
                annualMonthlyPrice: plan.pricing?.annualMonthlyPrice ?? 0,
                upgradeRoutesMonthly: plan.upgradeRoutes?.monthly || [],
                upgradeRoutesAnnual: plan.upgradeRoutes?.annual || [],
                downgradeRoutesMonthly: plan.downgradeRoutes?.monthly || [],
                downgradeRoutesAnnual: plan.downgradeRoutes?.annual || [],
                productPlans: plan.productPlans?.length > 0
                    ? plan.productPlans.map((pp: any) => ({ ...emptyProductPlan(), ...pp }))
                    : [],
            });
        } else {
            setFormData(initialFormData());
        }
        setError('');
    }, [open, plan]);

    const resolvedCurrency = useMemo(() => {
        if (!formData.country) return '';
        return envConfig.countryToCurrency[formData.country] || 'GBP';
    }, [formData.country, envConfig]);

    const currencySymbol = useMemo(() => {
        const curr = envConfig.currencies.find(c => c.code === resolvedCurrency);
        return curr?.symbol || resolvedCurrency;
    }, [resolvedCurrency, envConfig]);

    const targetPlanOptions = useMemo(() => {
        if (!formData.country) return [];
        const names = new Set<string>();
        allPlans
            .filter(p => p.country === formData.country)
            .forEach(p => names.add(p.planName));
        return Array.from(names).sort();
    }, [formData.country, allPlans]);

    const validationErrors = useMemo(() => {
        const errs: Record<string, string> = {};
        if (!formData.planName.trim()) errs.planName = 'Plan Name is required';
        if (!formData.country) errs.country = 'Country is required';
        if (!formData.rootDomain) errs.rootDomain = 'Root Domain is required';
        formData.productPlans.forEach((pp, idx) => {
            if (!pp.licenseType) errs[`productPlans.${idx}.licenseType`] = 'License type is required';
            if (!pp.module) errs[`productPlans.${idx}.module`] = 'Module is required';
            if (!pp.noOfUsers || pp.noOfUsers <= 0) errs[`productPlans.${idx}.noOfUsers`] = 'Must be > 0';
            if (!pp.durationMonths || pp.durationMonths <= 0) errs[`productPlans.${idx}.durationMonths`] = 'Must be > 0';
            if (pp.module === 'assetsmanagement.ai') {
                const value = pp.totalSizeInPlan;
                if (value === undefined || value === null || Number.isNaN(Number(value)) || Number(value) < 0) {
                    errs[`productPlans.${idx}.totalSizeInPlan`] = 'Cloud Storage Size is required';
                }
            }
        });
        return errs;
    }, [formData]);

    const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleProductPlanFieldChange = (index: number, field: string, value: any) => {
        setFormData(prev => {
            const plans = [...prev.productPlans];
            plans[index] = { ...plans[index], [field]: value };
            return { ...prev, productPlans: plans };
        });
    };

    const handleProductPlanQuotaChange = (index: number, field: string, value: any) => {
        setFormData(prev => {
            const plans = [...prev.productPlans];
            plans[index] = { ...plans[index], [field]: value ?? 0 };
            return { ...prev, productPlans: plans };
        });
    };

    const addProductPlan = () => {
        setFormData(prev => ({ ...prev, productPlans: [...prev.productPlans, emptyProductPlan()] }));
    };

    const removeProductPlan = (index: number) => {
        setFormData(prev => ({
            ...prev,
            productPlans: prev.productPlans.filter((_, i) => i !== index),
        }));
    };

    const updateRoute = (
        routeField: 'upgradeRoutesMonthly' | 'upgradeRoutesAnnual' | 'downgradeRoutesMonthly' | 'downgradeRoutesAnnual',
        index: number,
        field: keyof UpgradeRoute,
        value: string,
    ) => {
        setFormData(prev => {
            const routes = [...prev[routeField]];
            routes[index] = { ...routes[index], [field]: value };
            return { ...prev, [routeField]: routes };
        });
    };

    const addRoute = (routeField: 'upgradeRoutesMonthly' | 'upgradeRoutesAnnual' | 'downgradeRoutesMonthly' | 'downgradeRoutesAnnual') => {
        setFormData(prev => ({ ...prev, [routeField]: [...prev[routeField], emptyRoute()] }));
    };

    const removeRoute = (
        routeField: 'upgradeRoutesMonthly' | 'upgradeRoutesAnnual' | 'downgradeRoutesMonthly' | 'downgradeRoutesAnnual',
        index: number,
    ) => {
        setFormData(prev => ({
            ...prev,
            [routeField]: prev[routeField].filter((_, i) => i !== index),
        }));
    };

    const handleSave = async () => {
        setError('');
        if (Object.keys(validationErrors).length > 0) {
            setError('Please fix the validation errors before saving.');
            return;
        }

        const cleanedProductPlans = formData.productPlans
            .filter(pp => pp.licenseType && pp.module)
            .map(pp => {
                const plan: Record<string, any> = {
                    licenseType: pp.licenseType,
                    module: pp.module,
                    noOfUsers: Number(pp.noOfUsers) || 1,
                    durationMonths: Number(pp.durationMonths) || 1,
                };
                const numOrSkip = (val: any) => { const n = Number(val); return n ? n : undefined; };
                if (numOrSkip(pp.whatsAppPerDay)) plan.whatsAppPerDay = numOrSkip(pp.whatsAppPerDay);
                if (numOrSkip(pp.whatsAppTotalCredit)) plan.whatsAppTotalCredit = numOrSkip(pp.whatsAppTotalCredit);
                if (numOrSkip(pp.whatsAppTemplatePerDay)) plan.whatsAppTemplatePerDay = numOrSkip(pp.whatsAppTemplatePerDay);
                if (numOrSkip(pp.whatsAppTemplateTotalCredit)) plan.whatsAppTemplateTotalCredit = numOrSkip(pp.whatsAppTemplateTotalCredit);
                if (numOrSkip(pp.whatsAppBusinessPerDay)) plan.whatsAppBusinessPerDay = numOrSkip(pp.whatsAppBusinessPerDay);
                if (numOrSkip(pp.whatsAppBusinessTotalCredit)) plan.whatsAppBusinessTotalCredit = numOrSkip(pp.whatsAppBusinessTotalCredit);
                if (numOrSkip(pp.whatsAppBusinessTemplatePerDay)) plan.whatsAppBusinessTemplatePerDay = numOrSkip(pp.whatsAppBusinessTemplatePerDay);
                if (numOrSkip(pp.whatsAppBusinessTemplateTotalCredit)) plan.whatsAppBusinessTemplateTotalCredit = numOrSkip(pp.whatsAppBusinessTemplateTotalCredit);
                if (numOrSkip(pp.gsmTextPerDay)) plan.gsmTextPerDay = numOrSkip(pp.gsmTextPerDay);
                if (numOrSkip(pp.gsmTextTotalCredit)) plan.gsmTextTotalCredit = numOrSkip(pp.gsmTextTotalCredit);
                if (numOrSkip(pp.gsmTextTemplatePerDay)) plan.gsmTextTemplatePerDay = numOrSkip(pp.gsmTextTemplatePerDay);
                if (numOrSkip(pp.gsmTextTemplateTotalCredit)) plan.gsmTextTemplateTotalCredit = numOrSkip(pp.gsmTextTemplateTotalCredit);
                if (numOrSkip(pp.telegramPerDay)) plan.telegramPerDay = numOrSkip(pp.telegramPerDay);
                if (numOrSkip(pp.telegramTotalCredit)) plan.telegramTotalCredit = numOrSkip(pp.telegramTotalCredit);
                if (numOrSkip(pp.telegramTemplatePerDay)) plan.telegramTemplatePerDay = numOrSkip(pp.telegramTemplatePerDay);
                if (numOrSkip(pp.telegramTemplateTotalCredit)) plan.telegramTemplateTotalCredit = numOrSkip(pp.telegramTemplateTotalCredit);
                if (numOrSkip(pp.emailPerDay)) plan.emailPerDay = numOrSkip(pp.emailPerDay);
                if (numOrSkip(pp.emailTotalCredit)) plan.emailTotalCredit = numOrSkip(pp.emailTotalCredit);
                if (numOrSkip(pp.emailTemplatePerDay)) plan.emailTemplatePerDay = numOrSkip(pp.emailTemplatePerDay);
                if (numOrSkip(pp.emailTemplateTotalCredit)) plan.emailTemplateTotalCredit = numOrSkip(pp.emailTemplateTotalCredit);
                if (pp.senderHeaderName) plan.senderHeaderName = pp.senderHeaderName;
                if (pp.gsmWorkerName) plan.gsmWorkerName = pp.gsmWorkerName;
                return plan;
            });

        const filterValidRoutes = (routes: UpgradeRoute[]) =>
            routes.filter(r => r.targetPlan.trim() && r.targetCycle);

        const payload = {
            planName: formData.planName.trim(),
            tagline: formData.tagline.trim(),
            country: formData.country,
            rootDomain: formData.rootDomain,
            pricing: {
                monthlyPrice: Number(formData.monthlyPrice) || 0,
                annualMonthlyPrice: Number(formData.annualMonthlyPrice) || 0,
            },
            upgradeRoutes: {
                monthly: filterValidRoutes(formData.upgradeRoutesMonthly),
                annual: filterValidRoutes(formData.upgradeRoutesAnnual),
            },
            downgradeRoutes: {
                monthly: filterValidRoutes(formData.downgradeRoutesMonthly),
                annual: filterValidRoutes(formData.downgradeRoutesAnnual),
            },
            productPlans: cleanedProductPlans,
        };

        setSaving(true);
        try {
            if (isEditing) {
                await apiClient.put(`/businessplan/${plan._id}`, payload);
            } else {
                await apiClient.post('/businessplan/new', payload);
            }
            onSave();
        } catch (err: any) {
            const msg = err?.message || 'Failed to save business plan';
            setError(msg);
            Logger.error('BusinessPlanDialog', msg);
        } finally {
            setSaving(false);
        }
    };

    const renderRouteSection = (
        label: string,
        routeField: 'upgradeRoutesMonthly' | 'upgradeRoutesAnnual' | 'downgradeRoutesMonthly' | 'downgradeRoutesAnnual',
        routes: UpgradeRoute[],
    ) => (
        <Box mb={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Typography variant="caption" fontWeight={600}>{label}</Typography>
                <IconButton size="small" onClick={() => addRoute(routeField)}><Add fontSize="small" /></IconButton>
            </Box>
            {routes.map((route, idx) => (
                <Box key={idx} display="flex" gap={1} mb={0.5} alignItems="center">
                    <TextField
                        size="small"
                        label="Target Plan"
                        select
                        value={route.targetPlan}
                        onChange={(e) => updateRoute(routeField, idx, 'targetPlan', e.target.value)}
                        sx={{ flex: 1 }}
                        disabled={targetPlanOptions.length === 0}
                        helperText={!formData.country ? 'Select a country first' : undefined}
                    >
                        {targetPlanOptions.map((name) => (
                            <MenuItem key={name} value={name}>{name}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        label="Cycle"
                        select
                        value={route.targetCycle}
                        onChange={(e) => updateRoute(routeField, idx, 'targetCycle', e.target.value)}
                        sx={{ width: 120 }}
                    >
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="annual">Annual</MenuItem>
                    </TextField>
                    <IconButton size="small" color="error" onClick={() => removeRoute(routeField, idx)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ))}
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {isEditing ? 'Edit Business Plan' : 'Create Business Plan'}
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>
                )}
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    {/* Basic Info */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Plan Name *"
                            size="small"
                            fullWidth
                            value={formData.planName}
                            onChange={(e) => updateField('planName', e.target.value)}
                            error={!!validationErrors.planName}
                            helperText={validationErrors.planName}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={envConfig.countries}
                            getOptionLabel={(opt) => `${opt.code} - ${opt.label}`}
                            value={envConfig.countries.find(c => c.code === formData.country) || null}
                            onChange={(_e, val) => updateField('country', val?.code || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Country *"
                                    size="small"
                                    fullWidth
                                    error={!!validationErrors.country}
                                    helperText={validationErrors.country}
                                />
                            )}
                            isOptionEqualToValue={(opt, val) => opt.code === val.code}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Root Domain *"
                            size="small"
                            fullWidth
                            select
                            value={formData.rootDomain}
                            onChange={(e) => updateField('rootDomain', e.target.value)}
                            error={!!validationErrors.rootDomain}
                            helperText={validationErrors.rootDomain}
                        >
                            {(envConfig.rootDomains || []).map((domain) => (
                                <MenuItem key={domain} value={domain}>{domain}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Tagline"
                            size="small"
                            fullWidth
                            value={formData.tagline}
                            onChange={(e) => updateField('tagline', e.target.value)}
                        />
                    </Grid>

                    {/* Currency info */}
                    {formData.country && (
                        <Grid item xs={12}>
                            <Chip
                                label={`Currency: ${resolvedCurrency} (${currencySymbol}) — prices in lowest denomination`}
                                size="small"
                                color="info"
                                variant="outlined"
                            />
                        </Grid>
                    )}

                    {/* Pricing */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={`Monthly Price ${currencySymbol ? `(${currencySymbol})` : ''} — lowest denomination`}
                            size="small"
                            fullWidth
                            type="number"
                            value={formData.monthlyPrice}
                            onChange={(e) => updateField('monthlyPrice', Number(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={`Annual Monthly Price ${currencySymbol ? `(${currencySymbol})` : ''} — lowest denomination`}
                            size="small"
                            fullWidth
                            type="number"
                            value={formData.annualMonthlyPrice}
                            onChange={(e) => updateField('annualMonthlyPrice', Number(e.target.value))}
                        />
                    </Grid>

                    {/* Product Plans */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6" sx={{ mb: 2 }}>Product Plans</Typography>

                        {formData.productPlans.map((pp, idx) => (
                            <ProductPlanPanel
                                key={idx}
                                index={idx}
                                plan={pp as any}
                                onFieldChange={handleProductPlanFieldChange}
                                onQuotaChange={handleProductPlanQuotaChange}
                                onRemove={removeProductPlan}
                                licenseTypes={envConfig.licenseTypes}
                                modules={envConfig.modules}
                                errors={validationErrors}
                                variant="businessPlan"
                                workerPoolNames={workerPoolNames}
                                workerPoolNamesLoading={workerPoolNamesLoading}
                                workerPoolNamesError={workerPoolNamesError}
                            />
                        ))}

                        <Button
                            onClick={addProductPlan}
                            startIcon={<Add />}
                            variant="outlined"
                            color="secondary"
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            Add Product Plan
                        </Button>
                    </Grid>

                    {/* Upgrade / Downgrade Routes */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" fontWeight={700} mb={1}>Upgrade Routes</Typography>
                        {renderRouteSection('Monthly', 'upgradeRoutesMonthly', formData.upgradeRoutesMonthly)}
                        {renderRouteSection('Annual', 'upgradeRoutesAnnual', formData.upgradeRoutesAnnual)}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" fontWeight={700} mb={1}>Downgrade Routes</Typography>
                        {renderRouteSection('Monthly', 'downgradeRoutesMonthly', formData.downgradeRoutesMonthly)}
                        {renderRouteSection('Annual', 'downgradeRoutesAnnual', formData.downgradeRoutesAnnual)}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" color="secondary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BusinessPlanDialog;
